import { useEffect, useRef } from 'react'
import * as THREE from 'three'

interface OHLC {
  open: number
  high: number
  low: number
  close: number
}

interface CandleGroup {
  group: THREE.Group
  ohlc: OHLC
}

// Create upward arrow (bull symbol)
function createBullArrow(material: THREE.MeshBasicMaterial): THREE.Group {
  const group = new THREE.Group()

  // Arrow head (triangle)
  const headShape = new THREE.Shape()
  headShape.moveTo(0, 0.8)
  headShape.lineTo(-0.5, 0)
  headShape.lineTo(-0.2, 0)
  headShape.lineTo(-0.2, -0.8)
  headShape.lineTo(0.2, -0.8)
  headShape.lineTo(0.2, 0)
  headShape.lineTo(0.5, 0)
  headShape.closePath()

  const headGeometry = new THREE.ShapeGeometry(headShape)
  const head = new THREE.Mesh(headGeometry, material)
  group.add(head)

  return group
}

// Create trend line going up
function createTrendLine(material: THREE.MeshBasicMaterial): THREE.Group {
  const group = new THREE.Group()

  // Create ascending line using multiple small segments
  const points: THREE.Vector3[] = []
  const segments = 20

  for (let i = 0; i <= segments; i++) {
    const t = i / segments
    const x = t * 3 - 1.5
    // Smooth upward curve
    const y = Math.pow(t, 0.7) * 2 - 1
    points.push(new THREE.Vector3(x, y, 0))
  }

  // Create tube-like line
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i]
    const end = points[i + 1]
    const length = start.distanceTo(end)
    const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5)

    const lineGeometry = new THREE.BoxGeometry(length, 0.08, 0.04)
    const line = new THREE.Mesh(lineGeometry, material)
    line.position.copy(midPoint)

    // Rotate to connect points
    const angle = Math.atan2(end.y - start.y, end.x - start.x)
    line.rotation.z = angle

    group.add(line)
  }

  return group
}

// Generate realistic OHLC data with uptrend
function generateOHLC(prevClose: number, trendBias: number): OHLC {
  const volatility = 0.4 + Math.random() * 0.5
  const trend = trendBias * (0.05 + Math.random() * 0.1)

  const open = prevClose
  const change = (Math.random() - 0.4 + trend) * volatility
  const close = open + change

  const bodySize = Math.abs(close - open)

  // Realistic wicks - can be as long or longer than body
  // Upper wick: 30% to 150% of body size
  const upperWickMultiplier = 0.3 + Math.random() * 1.2
  // Lower wick: 30% to 150% of body size
  const lowerWickMultiplier = 0.3 + Math.random() * 1.2

  // Minimum wick size for visibility
  const minWick = 0.15

  const upperWick = Math.max(minWick, bodySize * upperWickMultiplier)
  const lowerWick = Math.max(minWick, bodySize * lowerWickMultiplier)

  const high = Math.max(open, close) + upperWick
  const low = Math.min(open, close) - lowerWick

  return { open, high, low, close }
}

// Create a single candlestick mesh group
function createCandlestick(
  ohlc: OHLC,
  x: number,
  candleWidth: number,
  bullishMaterial: THREE.MeshBasicMaterial,
  bearishMaterial: THREE.MeshBasicMaterial,
  wickMaterial: THREE.MeshBasicMaterial
): THREE.Group {
  const group = new THREE.Group()
  const isBullish = ohlc.close >= ohlc.open

  // Body
  const bodyHeight = Math.abs(ohlc.close - ohlc.open) || 0.02
  const bodyY = (ohlc.open + ohlc.close) / 2

  const bodyGeometry = new THREE.BoxGeometry(candleWidth, bodyHeight, candleWidth * 0.3)
  const body = new THREE.Mesh(bodyGeometry, isBullish ? bullishMaterial : bearishMaterial)
  body.position.set(0, bodyY, 0)
  group.add(body)

  // Upper wick
  const upperWickHeight = ohlc.high - Math.max(ohlc.open, ohlc.close)
  if (upperWickHeight > 0.01) {
    const upperWickGeometry = new THREE.BoxGeometry(candleWidth * 0.15, upperWickHeight, candleWidth * 0.1)
    const upperWick = new THREE.Mesh(upperWickGeometry, wickMaterial)
    upperWick.position.set(0, Math.max(ohlc.open, ohlc.close) + upperWickHeight / 2, 0)
    group.add(upperWick)
  }

  // Lower wick
  const lowerWickHeight = Math.min(ohlc.open, ohlc.close) - ohlc.low
  if (lowerWickHeight > 0.01) {
    const lowerWickGeometry = new THREE.BoxGeometry(candleWidth * 0.15, lowerWickHeight, candleWidth * 0.1)
    const lowerWick = new THREE.Mesh(lowerWickGeometry, wickMaterial)
    lowerWick.position.set(0, Math.min(ohlc.open, ohlc.close) - lowerWickHeight / 2, 0)
    group.add(lowerWick)
  }

  group.position.x = x

  return group
}

export function CandlestickBackground() {
  const containerRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<{
    scene: THREE.Scene
    camera: THREE.OrthographicCamera
    renderer: THREE.WebGLRenderer
    candles: CandleGroup[]
    bullSymbols: THREE.Group[]
    animationId: number
    scrollOffset: number
    lastPrice: number
    time: number
  } | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight
    const aspect = width / height

    // Scene
    const scene = new THREE.Scene()

    // Orthographic camera for 2D chart look
    const frustumSize = 12
    const camera = new THREE.OrthographicCamera(
      -frustumSize * aspect / 2,
      frustumSize * aspect / 2,
      frustumSize / 2,
      -frustumSize / 2,
      0.1,
      100
    )
    camera.position.set(0, 0, 10)
    camera.lookAt(0, 0, 0)

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    })
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setClearColor(0x000000, 0)
    container.appendChild(renderer.domElement)

    // Materials - white/gray monochrome
    const bullishMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.12,
    })

    const bearishMaterial = new THREE.MeshBasicMaterial({
      color: 0x888888,
      transparent: true,
      opacity: 0.08,
    })

    const wickMaterial = new THREE.MeshBasicMaterial({
      color: 0xaaaaaa,
      transparent: true,
      opacity: 0.1,
    })

    // Generate initial candles
    const candles: CandleGroup[] = []
    const candleCount = 60
    const candleWidth = 0.35
    const candleSpacing = 0.5
    const chartWidth = candleCount * candleSpacing

    let price = 0 // Start at center

    for (let i = 0; i < candleCount; i++) {
      const ohlc = generateOHLC(price, 0.6) // Uptrend bias
      price = ohlc.close

      const x = -chartWidth / 2 + i * candleSpacing
      const group = createCandlestick(ohlc, x, candleWidth, bullishMaterial, bearishMaterial, wickMaterial)

      scene.add(group)
      candles.push({ group, ohlc })
    }

    // Center the chart vertically based on price range
    const allPrices = candles.flatMap(c => [c.ohlc.high, c.ohlc.low])
    const minPrice = Math.min(...allPrices)
    const maxPrice = Math.max(...allPrices)
    const priceCenter = (minPrice + maxPrice) / 2

    // Offset all candles to center
    for (const candle of candles) {
      candle.group.position.y -= priceCenter
    }

    // Create bull market symbols
    const bullSymbols: THREE.Group[] = []

    const symbolMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.06,
    })

    // Add multiple arrows at different positions
    const arrowPositions = [
      { x: -8, y: 2, scale: 1.5 },
      { x: 6, y: -1.5, scale: 1.2 },
      { x: -3, y: 3.5, scale: 0.8 },
      { x: 10, y: 2.5, scale: 1.0 },
    ]

    for (const pos of arrowPositions) {
      const arrow = createBullArrow(symbolMaterial.clone())
      arrow.position.set(pos.x, pos.y, -1)
      arrow.scale.setScalar(pos.scale)
      scene.add(arrow)
      bullSymbols.push(arrow)
    }

    // Add trend lines
    const trendLineMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.04,
    })

    const trendLinePositions = [
      { x: -6, y: -2, scale: 1.2, rotation: 0.1 },
      { x: 4, y: 1, scale: 0.9, rotation: -0.05 },
    ]

    for (const pos of trendLinePositions) {
      const trendLine = createTrendLine(trendLineMaterial.clone())
      trendLine.position.set(pos.x, pos.y, -1)
      trendLine.scale.setScalar(pos.scale)
      trendLine.rotation.z = pos.rotation
      scene.add(trendLine)
      bullSymbols.push(trendLine)
    }

    // Store refs
    sceneRef.current = {
      scene,
      camera,
      renderer,
      candles,
      bullSymbols,
      animationId: 0,
      scrollOffset: 0,
      lastPrice: price - priceCenter,
      time: 0,
    }

    // Animation
    const scrollSpeed = 0.008
    const rightEdge = chartWidth / 2 + candleSpacing * 2
    const leftEdge = -rightEdge

    const animate = () => {
      if (!sceneRef.current) return

      sceneRef.current.scrollOffset += scrollSpeed
      sceneRef.current.time += 0.016 // ~60fps

      const time = sceneRef.current.time

      // Animate bull symbols
      for (let i = 0; i < sceneRef.current.bullSymbols.length; i++) {
        const symbol = sceneRef.current.bullSymbols[i]
        // Floating animation - each symbol has different phase
        const phase = i * 1.5
        symbol.position.y += Math.sin(time * 0.8 + phase) * 0.003
        // Subtle pulse scale
        const pulseScale = 1 + Math.sin(time * 1.2 + phase) * 0.02
        const baseScale = i < 4 ? [1.5, 1.2, 0.8, 1.0][i] : [1.2, 0.9][i - 4]
        symbol.scale.setScalar(baseScale * pulseScale)
        // Subtle opacity pulse via material
        symbol.traverse((child) => {
          if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshBasicMaterial) {
            const baseOpacity = i < 4 ? 0.06 : 0.04
            child.material.opacity = baseOpacity + Math.sin(time * 0.5 + phase) * 0.02
          }
        })
      }

      // Move all candles left
      for (const candle of sceneRef.current.candles) {
        candle.group.position.x -= scrollSpeed
      }

      // Check if leftmost candle needs to be recycled
      const firstCandle = sceneRef.current.candles[0]
      if (firstCandle && firstCandle.group.position.x < leftEdge) {
        // Remove from scene
        sceneRef.current.scene.remove(firstCandle.group)

        // Dispose geometries
        firstCandle.group.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose()
          }
        })

        // Remove from array
        sceneRef.current.candles.shift()

        // Create new candle on the right
        const lastCandle = sceneRef.current.candles[sceneRef.current.candles.length - 1]
        const newOhlc = generateOHLC(sceneRef.current.lastPrice, 0.6)
        sceneRef.current.lastPrice = newOhlc.close

        const newX = lastCandle.group.position.x + candleSpacing
        const newGroup = createCandlestick(newOhlc, newX, candleWidth, bullishMaterial, bearishMaterial, wickMaterial)

        // Keep it relatively centered but allow some drift for trend visibility
        if (Math.abs(sceneRef.current.lastPrice) > 3) {
          // Reset drift gradually
          sceneRef.current.lastPrice *= 0.95
          newGroup.position.y -= sceneRef.current.lastPrice * 0.1
        }

        sceneRef.current.scene.add(newGroup)
        sceneRef.current.candles.push({ group: newGroup, ohlc: newOhlc })
      }

      renderer.render(scene, camera)
      sceneRef.current.animationId = requestAnimationFrame(animate)
    }

    animate()

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !sceneRef.current) return

      const newWidth = containerRef.current.clientWidth
      const newHeight = containerRef.current.clientHeight
      const newAspect = newWidth / newHeight

      const cam = sceneRef.current.camera
      cam.left = -frustumSize * newAspect / 2
      cam.right = frustumSize * newAspect / 2
      cam.top = frustumSize / 2
      cam.bottom = -frustumSize / 2
      cam.updateProjectionMatrix()

      sceneRef.current.renderer.setSize(newWidth, newHeight)
    }

    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)

      if (sceneRef.current) {
        cancelAnimationFrame(sceneRef.current.animationId)

        for (const candle of sceneRef.current.candles) {
          candle.group.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.geometry.dispose()
              if (child.material instanceof THREE.Material) {
                child.material.dispose()
              }
            }
          })
          sceneRef.current.scene.remove(candle.group)
        }

        for (const symbol of sceneRef.current.bullSymbols) {
          symbol.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.geometry.dispose()
              if (child.material instanceof THREE.Material) {
                child.material.dispose()
              }
            }
          })
          sceneRef.current.scene.remove(symbol)
        }

        sceneRef.current.renderer.dispose()
      }

      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement)
      }
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  )
}
